import _ from 'lodash';
import * as util from '#packages/util';
import { HttpClient } from '@wix/http-client';
import {
  type Component as ComponentOld,
  ComponentType,
  type GetAppSnapshotResponse,
} from '@wix/ambassador-app-service-webapp/types';
import { getAppByVersion as getAppByVersionRequest } from '@wix/ambassador-devcenter-apps-v1-app-old/http';
import { queryMarketApp } from '@wix/ambassador-devcenter-market-apps-v1-market-app/http';
import {
  listLatestProductionVersions,
  listNotes,
  queryAppVersion,
} from '@wix/ambassador-devcenter-apps-v1-app-version/http';
import type { AppVersion } from '@wix/ambassador-devcenter-apps-v1-app-version/types';
import { SortOrder } from '@wix/ambassador-devcenter-apps-v1-app-version/types';
import type { VersionsResponse } from '@wix/ambassador-site-apps-server/types';
import experiment from 'experiment';
import { listMyApps } from '@wix/ambassador-blocks-v1-my-app/http';
import type { Component as BlocksComponent } from '@wix/ambassador-blocks-v1-my-app/types';

const APPS_SERVICES_BASE_URL = 'https://dev.wix.com/_api/app-service/v1/';
const httpClient = new HttpClient();

interface DeveloperAppData {
  appDefinitionId: string;
  appDefinitionName: string;
  latestVersion: string;
  dateCreated?: string;
  accountId?: string;
}

interface AvailableAppData extends DeveloperAppData {
  compTypes: ComponentType[];
  blocksVersion?: string;
  components: ComponentOld[];
}

const getAvailableApps = async (): Promise<AvailableAppData[]> => {
  const response = await httpClient.request(listMyApps({}));
  return (response.data.myApps ?? []).map((myApp) => {
    const components = (myApp.components ?? []).map(
      convertComponentDataToOldComponentData,
    );

    return {
      appDefinitionId: myApp.id ?? myApp.app?.id ?? '',
      appDefinitionName: myApp.app?.appData?.appName ?? '',
      latestVersion: myApp.app?.fullVersion || '',
      dateCreated: myApp.app?.createdDate?.toDateString(),
      accountId: myApp.app?.appData?.accountId,
      compTypes: getComponentTypes(components),
      blocksVersion: getBlocksVersion(components),
      components,
    };
  });
};

const convertComponentDataToOldComponentData = (
  componentData: BlocksComponent,
): ComponentOld => {
  return {
    compData: componentData.data as unknown as ComponentOld['compData'],
    compName: componentData.name as ComponentOld['compName'],
    compType: componentData.type as unknown as ComponentOld['compType'],
    compId: componentData.id as ComponentOld['compId'],
  };
};

const getComponentTypes = (components: ComponentOld[]): ComponentType[] =>
  _.uniq(components.map((compData) => compData.compType).filter(Boolean));

const getBlocksVersion = (components?: ComponentOld[]) => {
  const studioWidgetComponent = components?.find(
    ({ compType }) => compType === ComponentType.STUDIO_WIDGET,
  );

  if (!studioWidgetComponent) {
    // only Responsive Blocks (2.0.0) supports apps with CODE_PACKAGE or BACK_OFFICE_PAGE component
    const isResponsiveBlocks = components?.some(({ compType }) =>
      [ComponentType.CODE_PACKAGE, ComponentType.BACK_OFFICE_PAGE].includes(
        compType,
      ),
    );

    if (isResponsiveBlocks) return '2.0.0';
  }

  return studioWidgetComponent?.compData?.studioWidgetComponentData
    ?.blocksVersion;
};

const getAppInfoOld = (appDefIds: string[]) => {
  return util.http.fetchJson(
    `https://dev.wix.com/_api/app-service/v1/apps-info?apps-id=${appDefIds.join(
      '&apps-id=',
    )}`,
    { credentials: 'include' },
  );
};

const getAppSnapshotOld = (appDefinitionId: string) => {
  return util.http
    .fetchJson(`${APPS_SERVICES_BASE_URL}apps/${appDefinitionId}`)
    .then((res: GetAppSnapshotResponse) => {
      if (!res) {
        throw Error('Fetching app studio data from dev center failed');
      }
      return res;
    });
};

const getVersionsByAppId = async (appId: string) => {
  const appsVersionsResponse = await httpClient.request(
    queryAppVersion({
      query: {
        filter: { appId, state: 'PRODUCTION' },
        sort: [{ fieldName: 'updatedDate', order: SortOrder.DESC }],
      },
    }),
  );

  const oldVersionDataToNewVersionData = (appVersion: AppVersion) => {
    return {
      version: appVersion.fullVersion,
      dateUpdated: appVersion.updatedDate,
      dateCreated: appVersion.createdDate,
      status: 'RELEASED',
    };
  };

  return (
    (appsVersionsResponse?.data?.appVersions?.map(
      oldVersionDataToNewVersionData,
    ) as VersionsResponse[]) || []
  );
};
// todo: create experiments
const listingExperiment = experiment.isOpen('se_newDevCenterApiListing');
const namespaceExperiment = experiment.isOpen('se_newDevCenterApiNamespace');
const shareUrl = experiment.isOpen('se_newDevCenterApiShareUrl');

const fetchAppMarketData = async (apps: string[]) => {
  try {
    const { data } = await httpClient.request(
      queryMarketApp({
        query: {
          filter: {
            appId: apps,
          },
        },
      }),
    );

    return data?.marketApps;
  } catch (e) {
    return [];
  }
};

const fetchListLatestProductionVersions = async (appIds: string[]) => {
  const response = await httpClient.request(
    listLatestProductionVersions({ appIds }),
  );

  return response?.data?.appVersions;
};

export const getAppByVersion = async (
  appId: string,
  version: string,
  latestVersion: string,
) => {
  const { data: appByVersionResponse } = await httpClient.request(
    getAppByVersionRequest({
      appId,
      version,
    }),
  );

  const getAgregatedReleaseNotes = async () => {
    const listNotesResponse = await httpClient.request(
      listNotes({
        appId,
        version: util.appStudioUtils.getVersion(
          util.appStudioUtils.isTestVersion(version) ? latestVersion : version,
        ).major,
      }),
    );

    const notesArray = listNotesResponse?.data?.notes || [];
    return notesArray
      .filter((noteData) => !_.isEmpty(noteData.note))
      .map(
        (noteData) =>
          `${util.appStudioUtils.formatVersion(noteData.fullVersion)} ${
            noteData.note
          }`,
      );
  };

  const listReleaseNotes = await getAgregatedReleaseNotes();

  return {
    data: appByVersionResponse.data,
    listNotes: listReleaseNotes,
  };
};

export const devCenterFacade = {
  getAvailableApps,
  // todo: blocked by versions https://jira.wixpress.com/browse/WBL-4228
  getAppSnapshot: getAppSnapshotOld,
  getAppInfo: getAppInfoOld,
  // todo: namespace https://jira.wixpress.com/browse/WBL-4229 - createNamespacePanel.tsx
  namespaceSuggestion: namespaceExperiment,
  setNamespace: namespaceExperiment,
  // todo: listing https://jira.wixpress.com/browse/WBL-4231 - applicationStudioActions.ts
  updateName: listingExperiment,
  // todo: versions https://jira.wixpress.com/browse/WBL-4228 - fetchAppVersion.ts
  getAppByVersion,
  getVersionsByAppId,
  // todo: shareUrl https://jira.wixpress.com/browse/WBL-4230 - appLinkActions.ts
  fetchAppLinkFromDevCenter: shareUrl,
  createAppLinkFromDevCenter: shareUrl,
  revokeAppLinkFromDevCenter: shareUrl,
  fetchAppMarketData,
  fetchListLatestProductionVersions,
};
